import type { QueuedDraft } from './chat-input';

/**
 * FRONT-1163: persists the FRONT-761 queued-follow-up snapshot to
 * localStorage per chat session. Every chat window is remounted fresh per
 * chat (home.tsx / chats.tsx key the modal by item id), which used to drop
 * `queuedDraft` on the floor the instant the user switched away — this is
 * what survives that remount. Its own namespace/key (not draft-storage.ts's
 * `eddy.chat.draft.<key>`) because a queued draft carries attachments/image
 * state the plain draft-text box never had to.
 */

// pr-panel finding (2026-07-28): the stored shape had no age at all, so a
// draft abandoned for days/weeks would still be silently reconciled (and
// potentially auto-fired) against whatever turn happens to be current the
// next time that chat is opened — a message the user has long since
// forgotten about, sent with no fresh action on their part. A turn that
// hasn't settled within an hour is abandoned, not delayed; anything older
// is dropped on load rather than trusted.
const MAX_AGE_MS = 60 * 60 * 1000;

interface StoredQueuedDraft extends QueuedDraft {
  queuedAt: number;
}

function storageKey(sessionId: string): string {
  return `eddy.chat.queued.${sessionId}`;
}

/**
 * FRONT-1182: which id scopes this composer's QUEUED draft.
 *
 * Mirrors how the plain draft text is scoped (`draftKey ?? sessionId`, see
 * use-draft-text). These two halves of the same composer used to disagree:
 * the text draft honoured `draftKey`, the queued draft keyed off `sessionId`
 * alone. A surface that deliberately withholds `sessionId` — the coding
 * session does, because that prop also drives the chat model-mode queries it
 * has no use for — therefore had no key to persist a queued draft under, so
 * the FRONT-1163 persistence silently did nothing there and a queued
 * follow-up died with the composer on every project switch.
 *
 * Pure and exported so the agreement between the two halves is pinned by a
 * test rather than by two call sites happening to read the same way.
 */
export function queuedDraftKey(
  sessionId: string | undefined,
  draftKey: string | undefined,
): string | undefined {
  return draftKey ?? sessionId;
}

export function loadQueuedDraft(sessionId: string | undefined): QueuedDraft | null {
  if (!sessionId) return null;
  try {
    const raw = localStorage.getItem(storageKey(sessionId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredQueuedDraft> | null;
    if (!parsed || typeof parsed.text !== 'string' || !Array.isArray(parsed.attachments)) return null;
    if (typeof parsed.queuedAt === 'number' && Date.now() - parsed.queuedAt > MAX_AGE_MS) {
      localStorage.removeItem(storageKey(sessionId));
      return null;
    }
    const { queuedAt: _queuedAt, ...draft } = parsed;
    return draft as QueuedDraft;
  } catch {
    return null;
  }
}

export function persistQueuedDraft(sessionId: string | undefined, draft: QueuedDraft | null): void {
  if (!sessionId) return;
  try {
    if (draft) {
      const stored: StoredQueuedDraft = { ...draft, queuedAt: Date.now() };
      localStorage.setItem(storageKey(sessionId), JSON.stringify(stored));
    } else {
      localStorage.removeItem(storageKey(sessionId));
    }
  } catch {
    /* quota or storage disabled — queued draft becomes session-only, same as before this fix */
  }
}
